import axios from 'axios';
import {type ActionTree} from 'vuex';
import {CHAT_MESSAGE_TYPE_LEFT, CHAT_MESSAGE_TYPE_MESSAGE, CHAT_MESSAGE_TYPE_REMOVED} from '../../../../library/modules/chat/chat';
import {NoOperation} from '../../../../library/utils';
import {deepCopyObject, errorHandler} from '../../../../utils/portuna';
import dateHelper from '../../../../utils/portuna/dateHelper';

const actions: ActionTree<ModuleChatState, RootState> = {
    show({commit})
    {
        console.log('show chat store');
        commit('toggleVisibility', true);
        document.documentElement.classList.add('disable-scroll');
    },

    hide({
        commit,
        dispatch,
        getters,
    })
    {
        if (getters.chat)
        {
            dispatch('closeChat');
        }

        commit('toggleVisibility', false);
        document.documentElement.classList.remove('disable-scroll');
    },

    closeTab({
        commit,
        dispatch,
        getters,
    })
    {
        if (getters.chat)
        {
            dispatch('closeChat');
        }
        else
        {
            commit('setTab', null);
        }
    },

    openTab({
            commit,
            dispatch,
        },
        tab: ChatTabType,
    )
    {
        commit('setTab', tab);
    },

    openChat({
            commit,
            dispatch,
        },
        chat: Chat,
    )
    {
        commit('setChat', chat);
        dispatch('clearReply');
        dispatch('openTab', 'chat');
    },

    openDetails({commit})
    {
        commit('setDetailsOpen', true);
    },

    closeDetails({commit})
    {
        commit('setDetailsOpen', false);
    },

    editChat({
            commit,
            dispatch,
        },
        chat: Chat,
    )
    {
        commit('setChat', chat);
        dispatch('openTab', 'create-chat');
    },

    chatSaved({
            commit,
            dispatch,
            rootGetters,
        },
        {
            chat,
            open,
        }: { chat: Chat, open: boolean },
    )
    {
        commit(
            'updateChat',
            {
                savedChat: chat,
                currentUser: rootGetters.currentUser.id,
            },
        );

        if (open)
        {
            commit('setChat', chat);
            dispatch('openTab', 'chat');
        }
    },

    chatDeleted({
            commit,
            dispatch,
            getters,
            rootGetters,
        },
        deletedChat: number,
    )
    {
        if (getters.chat && getters.chat.id === deletedChat)
        {
            dispatch('closeChat').then(() =>
            {
                commit('deleteChat', deletedChat);
            });
        }
        else
        {
            commit('deleteChat', deletedChat);
        }
    },

    chatLeft({
            commit,
            dispatch,
            getters,
            rootGetters,
        },
        payload: { chat: number, user: number },
    )
    {
        commit('participantLeft', payload);
    },

    chatStatusChanged({
            commit,
            dispatch,
            getters,
            rootGetters,
        },
        chat: number,
    )
    {
        commit('toggleChatStatus', chat);
    },

    closeChat({
        commit,
        dispatch,
    })
    {
        dispatch('openTab', null).then(() =>
        {
            dispatch('clearReply');
            commit('setChat', null);
        });
    },

    updateChatLastReadToNow({commit}, chat: number)
    {
        commit(
            'updateChatLastRead',
            {
                chat,
                timestamp: dateHelper('Y-m-d H:i:s'),
            },
        );
    },

    updateChatLastMessageRead({commit}, chat: number)
    {
        commit('updateChatLastMessageRead', {chat});
    },

    readChat({
        commit,
        dispatch,
    }, chat: number)
    {
        axios.post('/chat/chat/set-read', {chat})
        .finally(NoOperation);
    },

    fetchChats({
        state,
        getters,
        commit,
    })
    {
        let postData: object = {
            lastUpdate: state.lastUpdate,
            currentChat: getters.chat?.id ?? null,
        };

        axios.post('/chat/chat/fetch-chat-data', postData)
        .then(({data: {data}}) =>
        {
            commit('lastUpdate', data.lastUpdate);
            commit('participantChats', data.participantChat);
            commit('strangerChats', data.strangerChat);
            commit('isAdmin', data.isAdmin);
        }).catch(errorHandler);
    },

    newMessage({
        commit,
        getters,
        rootGetters,
        dispatch,
    }, message: ChatNewMessage)
    {
        commit(
            'updateChatLastMessage',
            {
                message,
                currentUser: rootGetters.currentUser.id,
            },
        );

        if (message.type !== CHAT_MESSAGE_TYPE_MESSAGE)
        {
            const userId = parseInt(message.text);
            if (!userId)
            {
                return;
            }

            dispatch('findChat', {chatId: message.chat_id}).then((chat: Chat) =>
            {
                let chatCopy: Chat = deepCopyObject(chat);

                if ([CHAT_MESSAGE_TYPE_LEFT, CHAT_MESSAGE_TYPE_REMOVED].includes(message.type))
                {
                    for (const index in chatCopy.participants)
                    {
                        if (!chatCopy.participants.hasOwnProperty(index))
                        {
                            continue;
                        }

                        if (chatCopy.participants[index].user_id === userId)
                        {
                            chatCopy.participants.splice(parseInt(index), 1);
                        }
                    }

                    dispatch(
                        'chatSaved',
                        {
                            chat: chatCopy,
                            open: false,
                        },
                    );
                }
            }).catch(NoOperation);
        }
    },

    findChat({getters}, {
        chatId,
        searchIn,
    }: FindChatOptions)
    {
        let scope: Chat[];

        searchIn = searchIn || 'both';

        if ('both' === searchIn)
        {
            scope = ([] as Chat[]).concat(getters.participantChats, getters.strangerChats);
        }
        else if ('participant' === searchIn)
        {
            scope = getters.participantChats;
        }
        else
        {
            scope = getters.strangerChats;
        }

        for (const chat of scope)
        {
            if (chat.id === chatId)
            {
                return chat;
            }
        }

        throw new Error();
    },

    setReply({commit}, message: ChatMessage)
    {
        commit('setReply', message);
    },

    clearReply({commit})
    {
        commit('setReply', null);
    },
};

export default actions;